/**
 * Moderator Ticket Detail Page
 * Extended ticket detail page with moderator actions
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  ButtonGroup,
} from '@mui/material'
import {
  ArrowBack,
  PlayArrow,
  HourglassBottom,
  CheckCircle,
  AddCircleOutline,
  RemoveCircleOutline,
  Refresh as RefreshIcon,
  Block as CloseIcon,
  Warning as EscalateIcon,
} from '@mui/icons-material'
import {
  getTicketDetails,
  addComment,
  claimTicket,
  unclaimTicket,
  startProgress,
  markAwaitingUser,
  resolveTicket,
  reopenTicket,
  closeTicket,
  escalateTicket,
} from '@api/services/tickets.api'
import { TicketDetailsDto, AddCommentRequest, TicketStatus } from '@api/types/tickets'
import { TicketStatusBadge } from '../components/TicketStatusBadge'
import { TicketPriorityIndicator } from '../components/TicketPriorityIndicator'
import { TicketTypeIcon } from '../components/TicketTypeIcon'
import { TicketCommentThread } from '../components/TicketCommentThread'
import { TicketTargetLink } from '../components/TicketTargetLink'
import { useAuthStore } from '@store/authStore'
import { safeFormatDistanceToNow } from '@utils/dateUtils'

export default function ModeratorTicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [ticket, setTicket] = useState<TicketDetailsDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadTicket = async () => {
    if (!ticketId) return

    setLoading(true)
    setError(null)
    try {
      const data = await getTicketDetails(ticketId)
      setTicket(data)
    } catch (err: any) {
      console.error('Failed to load ticket:', err)
      setError(err.message || 'Failed to load ticket')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTicket()
  }, [ticketId])

  const handleAddComment = async (request: AddCommentRequest) => {
    if (!ticketId) return

    setIsSubmitting(true)
    try {
      await addComment(ticketId, request)
      await loadTicket()
    } catch (error) {
      console.error('Failed to add comment:', error)
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClaim = async () => {
    if (!ticketId) return
    setIsSubmitting(true)
    try {
      const updatedTicket = await claimTicket(ticketId)
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to claim ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUnclaim = async () => {
    if (!ticketId) return
    setIsSubmitting(true)
    try {
      const updatedTicket = await unclaimTicket(ticketId)
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to unclaim ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleStartProgress = async () => {
    if (!ticketId) return
    setIsSubmitting(true)
    try {
      const updatedTicket = await startProgress(ticketId)
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to start progress')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleMarkAwaitingUser = async () => {
    if (!ticketId) return
    setIsSubmitting(true)
    try {
      const updatedTicket = await markAwaitingUser(ticketId)
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to mark as awaiting user')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResolve = async () => {
    if (!ticketId) return

    const notes = window.prompt('Enter resolution notes:')
    if (!notes) return

    setIsSubmitting(true)
    try {
      const updatedTicket = await resolveTicket(ticketId, { resolutionNotes: notes })
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to resolve ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReopen = async () => {
    if (!ticketId) return
    setIsSubmitting(true)
    try {
      const updatedTicket = await reopenTicket(ticketId)
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to reopen ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = async () => {
    if (!ticketId) return
    if (!confirm('Are you sure you want to close this ticket? It can be reopened if needed.')) return

    setIsSubmitting(true)
    try {
      const updatedTicket = await closeTicket(ticketId)
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to close ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEscalate = async () => {
    if (!ticketId) return

    const reason = window.prompt('Enter escalation reason:')
    if (!reason) return

    setIsSubmitting(true)
    try {
      const updatedTicket = await escalateTicket(ticketId, { reason })
      setTicket(updatedTicket)
    } catch (err: any) {
      setError(err.message || 'Failed to escalate ticket')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate('/tickets')
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error || !ticket) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Ticket not found'}
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleBack}>
          Back to Tickets
        </Button>
      </Container>
    )
  }

  // Normalize IDs for comparison (handle case, whitespace, hyphens in GUIDs)
  const normalizeId = (id: string | null | undefined): string => {
    if (!id) return ''
    return id.toLowerCase().trim().replace(/[-\s]/g, '')
  }

  const isClaimed = normalizeId(ticket.claimedByUserId) === normalizeId(user?.userId)
  const canClaim = ticket.status === TicketStatus.Pending
  const canUnclaim = isClaimed && ticket.status === TicketStatus.Claimed
  const canStartProgress = isClaimed && ticket.status === TicketStatus.Claimed
  const canMarkAwaiting = isClaimed && ticket.status === TicketStatus.InProgress
  const canResolve =
    isClaimed && (ticket.status === TicketStatus.InProgress || ticket.status === TicketStatus.AwaitingUser)
  const canClose = ticket.status === TicketStatus.Resolved
  const canReopen = ticket.status === TicketStatus.Resolved || ticket.status === TicketStatus.Closed
  const canEscalate = isClaimed && (ticket.status === TicketStatus.Claimed || ticket.status === TicketStatus.InProgress || ticket.status === TicketStatus.AwaitingUser) && !ticket.isEscalated

  // Debug logging
  console.log('=== TICKET AUTH DEBUG ===')
  console.log('Auth Store User:', user)
  console.log('User ID from adventurecore-auth:', user?.userId)
  console.log('User DisplayName:', user?.displayName)
  console.log('User Roles:', user?.roles)
  console.log('---')
  console.log('Ticket Status:', ticket.status, '(' + TicketStatus[ticket.status] + ')')
  console.log('Ticket claimedByUserId:', ticket.claimedByUserId)
  console.log('Ticket claimedByDisplayName:', ticket.claimedByDisplayName)
  console.log('---')
  console.log('Normalized claimedByUserId:', normalizeId(ticket.claimedByUserId))
  console.log('Normalized user.userId:', normalizeId(user?.userId))
  console.log('IDs Match (isClaimed):', isClaimed)
  console.log('---')
  console.log('Action Flags:', {
    canClaim,
    canUnclaim,
    canStartProgress,
    canMarkAwaiting,
    canResolve,
    canClose,
    canReopen,
    canEscalate
  })
  console.log('=========================')

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button variant="text" startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Ticket Management
        </Button>

        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <TicketTypeIcon type={ticket.type} size="large" />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h4" gutterBottom>
                {ticket.title}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <TicketStatusBadge status={ticket.status} />
                <TicketPriorityIndicator priority={ticket.priority} />
                {ticket.isEscalated && <Chip label="ESCALATED" color="error" size="small" />}
              </Box>

              <Typography variant="body2" color="text.secondary">
                Created {safeFormatDistanceToNow(ticket.createdAt)} by{' '}
                <strong>{ticket.createdByDisplayName || 'Unknown'}</strong>
              </Typography>
            </Box>
          </Box>

          {/* Workflow Status Helper */}
          {isClaimed && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {ticket.status === TicketStatus.Claimed && "✓ Ticket claimed - Click 'Start Progress' when you begin working"}
              {ticket.status === TicketStatus.InProgress && "✓ In progress - Mark as 'Await User' if you need user input, or 'Resolve' when fixed"}
              {ticket.status === TicketStatus.AwaitingUser && "✓ Awaiting user response - Continue to 'Resolve' when ready"}
              {ticket.status === TicketStatus.Resolved && "✓ Resolved - Anyone can 'Close' to confirm completion"}
            </Alert>
          )}

          {/* Moderator Actions */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Moderator Actions
            </Typography>

            {/* Show message if no actions available */}
            {!canClaim && !canUnclaim && !canStartProgress && !canMarkAwaiting && !canResolve && !canClose && !canReopen && !canEscalate ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {ticket.claimedByUserId && ticket.claimedByUserId !== user?.userId ? (
                  <>This ticket is being handled by <strong>{ticket.claimedByDisplayName}</strong>. Only the assigned moderator can take actions on claimed tickets.</>
                ) : (
                  <>No actions available for this ticket in its current state.</>
                )}
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* Primary workflow actions */}
                <ButtonGroup variant="contained" size="small">
                  {canClaim && (
                    <Button startIcon={<AddCircleOutline />} onClick={handleClaim} disabled={isSubmitting}>
                      Claim Ticket
                    </Button>
                  )}
                  {canUnclaim && (
                    <Button startIcon={<RemoveCircleOutline />} onClick={handleUnclaim} disabled={isSubmitting}>
                      Unclaim
                    </Button>
                  )}
                  {canStartProgress && (
                    <Button startIcon={<PlayArrow />} onClick={handleStartProgress} disabled={isSubmitting}>
                      Start Progress
                    </Button>
                  )}
                  {canMarkAwaiting && (
                    <Button startIcon={<HourglassBottom />} onClick={handleMarkAwaitingUser} disabled={isSubmitting}>
                      Await User
                    </Button>
                  )}
                  {canResolve && (
                    <Button startIcon={<CheckCircle />} color="success" onClick={handleResolve} disabled={isSubmitting}>
                      Resolve
                    </Button>
                  )}
                  {canClose && (
                    <Button startIcon={<CloseIcon />} color="info" onClick={handleClose} disabled={isSubmitting}>
                      Close
                    </Button>
                  )}
                  {canReopen && (
                    <Button startIcon={<RefreshIcon />} color="warning" onClick={handleReopen} disabled={isSubmitting}>
                      Reopen
                    </Button>
                  )}
                </ButtonGroup>

                {/* Secondary actions */}
                {canEscalate && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EscalateIcon />}
                    color="error"
                    onClick={handleEscalate}
                    disabled={isSubmitting}
                  >
                    Escalate
                  </Button>
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Description */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {ticket.description}
            </Typography>
          </Box>

          {/* Tags */}
          {ticket.tags && ticket.tags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {ticket.tags.map((tag: string | { id: string; displayName?: string; name?: string }) => {
                  const tagId = typeof tag === 'string' ? tag : tag.id
                  const tagLabel = typeof tag === 'string' ? tag : (tag.displayName || tag.name)
                  return (
                    <Chip key={tagId} label={tagLabel} size="small" />
                  )
                })}
              </Box>
            </Box>
          )}

          {/* Reported Content Link */}
          {(ticket.targetType !== null && ticket.targetId !== null) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Reported Content
              </Typography>
              <TicketTargetLink
                targetType={ticket.targetType}
                targetId={ticket.targetId}
                variant="chip"
              />
            </Box>
          )}

          {/* Metadata Grid */}
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Ticket ID
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {ticket.id.slice(0, 8)}...
              </Typography>
            </Grid>

            {ticket.claimedByDisplayName && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Claimed By
                </Typography>
                <Typography variant="body2">{ticket.claimedByDisplayName}</Typography>
              </Grid>
            )}

            {ticket.assignedToDisplayName && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Assigned To
                </Typography>
                <Typography variant="body2">{ticket.assignedToDisplayName}</Typography>
              </Grid>
            )}

            {ticket.resolvedByDisplayName && (
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Resolved By
                </Typography>
                <Typography variant="body2">{ticket.resolvedByDisplayName}</Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="caption" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body2">
                {safeFormatDistanceToNow(ticket.lastUpdatedAt)}
              </Typography>
            </Grid>
          </Grid>

          {/* Resolution Notes */}
          {ticket.resolutionNotes && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="success">
                <Typography variant="subtitle2" gutterBottom>
                  Resolution Notes
                </Typography>
                <Typography variant="body2">{ticket.resolutionNotes}</Typography>
              </Alert>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Comments Section */}
      <TicketCommentThread
        comments={ticket.comments}
        onAddComment={handleAddComment}
        canAddInternalComments={true}
        isSubmitting={isSubmitting}
      />
    </Container>
  )
}
